import { S3_BASE_URL } from '../constants/constants';

// ─── Utility: fire Lambda without waiting for sheet generation ──────────────────
const KEEPALIVE_LIMIT = 64_000; // spec cap for keepalive request bodies (~64KB)

/**
 * POST to a Lambda and return immediately — the Lambda generates sheets in the
 * background, so we never wait for it to finish (the response is ignored).
 *
 * `keepalive` lets the request survive the page being torn down by the parent's
 * add-to-cart navigation, but the spec caps keepalive bodies at ~64KB. Bigger
 * payloads simply omit it. Failures are always logged via `.catch`, never
 * swallowed silently. Call this WITHOUT `await` so the user isn't blocked.
 */
export const fireLambda = (url, data) => {
  const body = JSON.stringify(data);
  const fitsKeepalive = new Blob([body]).size < KEEPALIVE_LIMIT;

  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: fitsKeepalive,
  }).catch(err => console.error('[lambda] request failed:', url, err));
};

// ─── Utility: debounce ──────────────────────────────────────────────────────────
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// ─── Utility: merge duplicate sheets ────────────────────────────────────────────
/**
 * Build a stable content signature for a single sheet so that two sheets which
 * represent the same printed output collapse to the same key.
 *
 * - `extraKeys` are top-level fields that also distinguish a sheet (e.g. width /
 *   height for suggested sheets). For manual sheets the size is fixed for the whole
 *   batch, so the packed-image content is what differentiates them.
 * - The signature is derived from `packedImages` (the actual placed images). Each
 *   entry is stringified and the list is sorted, so a different ordering of the same
 *   images does not produce a false "unique" sheet. Volatile fields such as the
 *   generated `imgUrl` and the running `quantity` are deliberately excluded.
 */
export function sheetContentSignature(sheet, extraKeys = []) {
  if (!sheet || typeof sheet !== 'object') return String(sheet);

  const base = extraKeys.map(k => `${k}:${sheet[k]}`).join('|');

  let contentSig;
  if (Array.isArray(sheet.packedImages)) {
    contentSig = sheet.packedImages
      .map(p => JSON.stringify(p))
      .sort()
      .join('|');
  } else {
    // No packedImages — fall back to the whole object minus volatile fields.
    const { imgUrl, quantity, ...rest } = sheet;
    contentSig = JSON.stringify(rest);
  }

  return `${base}#${contentSig}`;
}

/**
 * Collapse identical sheets into a single entry, summing their quantities, while
 * preserving first-seen order. Sheets the Lambda returns without an explicit
 * `quantity` are treated as quantity 1.
 *
 * @param {Array}    sheets  raw sheet list from the Lambda
 * @param {Function} keyFn   (sheet) => string signature used for grouping
 * @returns {Array}  merged sheets, each with a numeric summed `quantity`
 */
export function mergeDuplicateSheets(sheets, keyFn = sheetContentSignature) {
  if (!Array.isArray(sheets)) return [];
  const map = new Map();
  for (const sheet of sheets) {
    const key = keyFn(sheet);
    const qty = Number(sheet?.quantity) > 0 ? Number(sheet.quantity) : 1;
    const existing = map.get(key);
    if (existing) {
      existing.quantity += qty;
    } else {
      map.set(key, { ...sheet, quantity: qty });
    }
  }
  return Array.from(map.values());
}

// ─── Utility: preload images from S3 ───────────────────────────────────────────
export function preloadImages(data) {
  return Promise.all(
    data.map(item => new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.src = S3_BASE_URL + item.path;
      img.onload = () => resolve({ img, item });
      img.onerror = () => reject(new Error(`Failed to load ${item.path}`));
    }))
  );
}
